// ABOUTME: Custom hook for SharePoint file selection logic and state management
// ABOUTME: Handles file selection, modal state, and form integration with react-hook-form

import { useCallback, useState } from 'react';
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form';

import type { ISharePointItem } from '../api/types';

type IUseFileSelectionProps<T extends FieldValues> = {
    readonly name: FieldPath<T>;
    readonly control: Control<T>;
    readonly hierarchyId?: string;
    readonly disabled?: boolean;
    readonly readOnly?: boolean;
};

type IUseFileSelectionReturn = {
    readonly selectedFile: ISharePointItem | null;
    readonly isModalOpen: boolean;
    readonly handleFileSelect: (files: ISharePointItem[]) => void;
    readonly handleClearSelection: () => void;
    readonly handleOpenModal: () => void;
    readonly handleCloseModal: () => void;
};

export const useFileSelection = <T extends FieldValues>({
    name,
    control,
    hierarchyId,
    disabled = false,
    readOnly = false,
}: IUseFileSelectionProps<T>): IUseFileSelectionReturn => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { field } = useController({
        name,
        control,
        defaultValue: null as T[FieldPath<T>],
    });

    const selectedFile: ISharePointItem | null = field.value;

    const handleFileSelect = useCallback(
        (files: ISharePointItem[]) => {
            const file = files[0];
            field.onChange(file);
            setIsModalOpen(false);
        },
        [field],
    );

    const handleClearSelection = useCallback(() => {
        field.onChange('');
    }, [field]);

    const handleOpenModal = useCallback(() => {
        if (!disabled && !readOnly && hierarchyId) {
            setIsModalOpen(true);
        }
    }, [disabled, readOnly, hierarchyId]);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    return {
        selectedFile,
        isModalOpen,
        handleFileSelect,
        handleClearSelection,
        handleOpenModal,
        handleCloseModal,
    };
};
